import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  ContentCopy,
  CheckCircle,
  PowerSettingsNew,
  OpenInNew,
} from '@mui/icons-material';
import api from '../../utils/api';

interface CreatePropertyLink {
  id: string;
  token: string;
  expiresAt: string;
  usageCount: number;
  maxUsages: number;
  lastUsedAt: string | null;
  isActive: boolean;
  createdAt: string;
}

interface GeneratePropertyLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onLinkCreated?: () => void;
}

const GeneratePropertyLinkDialog: React.FC<GeneratePropertyLinkDialogProps> = ({
  open,
  onClose,
  onLinkCreated,
}) => {
  const [step, setStep] = useState<'configure' | 'view' | 'manage'>('configure');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedLink, setGeneratedLink] = useState<CreatePropertyLink | null>(null);
  const [existingLinks, setExistingLinks] = useState<CreatePropertyLink[]>([]);
  const [copied, setCopied] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);

  useEffect(() => {
    if (open && step === 'manage') {
      fetchExistingLinks();
    }
  }, [open, step]);

  const fetchExistingLinks = async () => {
    try {
      setLoadingLinks(true);
      const response = await api.get('/property-creation-links');
      setExistingLinks(response.data);
    } catch (err: any) {
      console.error('Failed to fetch links:', err);
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.post('/property-creation-links', { maxUsages: 1 });

      // Normalise response — backend may return the link nested or directly
      const linkObj: CreatePropertyLink =
        response.data?.link ??
        response.data?.creationLink ??
        response.data?.data ??
        response.data;

      console.log('[GenerateLink] raw response:', response.data);
      console.log('[GenerateLink] resolved link:', linkObj);

      if (!linkObj?.token) {
        setError('Link was created but the token is missing in the response. Check console for details.');
        return;
      }

      setGeneratedLink(linkObj);
      setStep('view');
      setSuccess('Creation link generated successfully!');
      onLinkCreated?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const getFullLink = (token: string): string => {
    const base = process.env.REACT_APP_APP_URL?.replace(/\/$/, '') || window.location.origin;
    return `${base}/create-property/${token}`;
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      const fullLink = getFullLink(generatedLink.token);
      navigator.clipboard.writeText(fullLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDeactivateLink = async (linkId: string) => {
    try {
      await api.patch(`/property-creation-links/${linkId}/deactivate`, {});
      setExistingLinks(prev =>
        prev.map(link => (link.id === linkId ? { ...link, isActive: false } : link))
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate link');
    }
  };

  const handleClose = () => {
    setStep('configure');
    setError('');
    setSuccess('');
    setGeneratedLink(null);
    setCopied(false);
    onClose();
  };

  const getRemainingUsages = (link: CreatePropertyLink) => {
    if (link.maxUsages === -1) return 'Unlimited';
    const remaining = link.maxUsages - link.usageCount;
    return remaining <= 0 ? 'Used' : `${remaining}/${link.maxUsages}`;
  };

  const getStatusChip = (link: CreatePropertyLink) => {
    if (!link.isActive) {
      return <Chip label="Inactive" size="small" color="error" variant="outlined" />;
    }
    const expiresAt = new Date(link.expiresAt);
    const now = new Date();
    if (expiresAt < now) {
      return <Chip label="Expired" size="small" color="error" variant="outlined" />;
    }
    return <Chip label="Active" size="small" color="success" variant="outlined" />;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', pb: 0 }}>
        Create Property Link
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* Configure Step */}
        {step === 'configure' && (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate a <strong>one-time use link</strong> to allow someone outside the system to submit a single property to your database. The link becomes invalid after one submission.
            </Typography>

            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f7ff', borderRadius: 2, border: '1px solid #1967d2' }}>
              <Typography variant="caption" color="text.secondary">
                ℹ️ Link Details:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • Valid for 2 hours from creation
                <br />
                • <strong>One-time use only</strong> — expires immediately after one submission
                <br />
                • The submitted property will be added in <strong>pending status</strong>
                <br />
                • You can deactivate the link at any time before it's used
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setStep('manage')}
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
              >
                View Existing
              </Button>
            </Box>
          </Box>
        )}

        {step === 'view' && generatedLink && (
          <Box>
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Share This Link:
            </Typography>

            {/* Selectable link field with copy + open buttons */}
            <TextField
              fullWidth
              value={getFullLink(generatedLink.token)}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.8rem', pr: 0.5 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleCopyLink}
                      title="Copy link"
                      sx={{ color: copied ? 'success.main' : 'text.secondary' }}
                    >
                      {copied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => window.open(getFullLink(generatedLink.token), '_blank')}
                      title="Open in new tab"
                      sx={{ color: 'text.secondary' }}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0fdf4', borderRadius: 2, border: '1px solid #22c55e' }}>
              <Typography variant="caption" fontWeight={600} color="#166534">
                ✓ One-Time Link Created
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#166534' }}>
                • Expires: {new Date(generatedLink.expiresAt).toLocaleString()}
                <br />
                • Usage: <strong>1 use only</strong> — auto-invalidates after first submission
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Share this link with the person who needs to submit the property. It can only be used once.
            </Typography>
          </Box>
        )}

        {/* Manage Links Step */}
        {step === 'manage' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              All creation links you've generated:
            </Typography>

            {loadingLinks ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : existingLinks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No creation links generated yet.
              </Typography>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#f5f7fa' }}>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Usages</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {existingLinks.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell>{getStatusChip(link)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {getRemainingUsages(link)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(getFullLink(link.token));
                            }}
                            disabled={!link.isActive}
                            title="Copy link"
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => window.open(getFullLink(link.token), '_blank')}
                            disabled={!link.isActive}
                            title="Open link"
                          >
                            <OpenInNew sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivateLink(link.id)}
                            disabled={!link.isActive}
                            title="Deactivate"
                            sx={{ color: link.isActive ? 'error.main' : 'text.disabled' }}
                          >
                            <PowerSettingsNew sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        {step === 'view' && (
          <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Done
          </Button>
        )}
        {step === 'configure' && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#1967d2' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Generate Link'}
            </Button>
          </>
        )}
        {step === 'manage' && (
          <>
            <Button onClick={() => setStep('configure')} sx={{ textTransform: 'none' }}>
              Back
            </Button>
            <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
              Done
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GeneratePropertyLinkDialog;
